import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';

import { BaseContainer, InlineDescriptionListItem, isNotUndefined, NavigationItem } from '@stylehaven/components';

import { isEqual } from 'lodash-es';
import { SubjectRejection } from '../../../entities/common/subject-rejection.entity';
import { NotificationItem } from '../../../entities/notifications/notification-item.entity';
import { RecordIndex } from '../../../state/overview-page-cache/overview-page-cache.entity';
import { OverviewOptions } from '../../../state/overview/overview-options.entity';
import { RecordHeaderTabItem } from '../../entities/record-header-tab-item.entity';
import { RejectionTranslations } from '../../entities/rejection-translations.entity';
import { RecordHeaderSandboxService } from '../../sandbox/record-header-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-record-header',
    templateUrl: './record-header-container.component.html',
    styleUrls: ['./record-header-container.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordHeaderContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    @Input() public title!: string;
    @Input() public contextItems: InlineDescriptionListItem[] | undefined;

    @Input() set recordId(recordId: string) {
        if (recordId) {
            this.recordId$.next(recordId);
        }
    }

    @Input() overviewContextToIgnore?: string = undefined;
    @Input() rejections?: SubjectRejection[];
    @Input() rejectionTranslations!: RejectionTranslations;
    @Input() notification?: NotificationItem;
    @Input() warningNotification?: NotificationItem;

    @Input() set tabItems(tabItems: RecordHeaderTabItem[]) {
        if (tabItems) {
            this.tabItems$.next(tabItems);
        }
    }

    @Input() isEditing = false;
    @Input() set showChildRejections(showChildRejections: boolean) {
        this.showChildRejections$.next(showChildRejections);
    }

    recordId$: ReplaySubject<string> = new ReplaySubject<string>(1);
    title$: ReplaySubject<string> = new ReplaySubject<string>(1);
    tabItems$: ReplaySubject<RecordHeaderTabItem[]> = new ReplaySubject<RecordHeaderTabItem[]>(1);
    showChildRejections$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public primaryNav$!: Observable<NavigationItem[]>;
    public secondaryNav$!: Observable<NavigationItem[]>;

    private overview$!: Observable<OverviewOptions | undefined>;
    private index$!: Observable<RecordIndex>;
    tabItemsInternal$!: Observable<RecordHeaderTabItem[]>;

    constructor(
        protected readonly activatedRoute: ActivatedRoute,
        private readonly sandbox: RecordHeaderSandboxService,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.initOverviewId();
        this.addOverviewToQueryParamsOfTabItems();
        this.initIndex();
        this.initQueryContextElements();
    }

    private initOverviewId(): void {
        this.overview$ = this.activatedRoute.queryParamMap.pipe(
            map(params => params.get('overview')),
            switchMap(overviewId => {
                if (overviewId) {
                    return this.sandbox.getOverview(overviewId);
                } else {
                    return this.sandbox.getOverviewFromStack(this.overviewContextToIgnore);
                }
            })
        );
    }

    private addOverviewToQueryParamsOfTabItems(): void {
        this.tabItemsInternal$ = combineLatest([this.tabItems$, this.overview$]).pipe(
            takeUntil(this.destroy$),
            map(([tabItems, overview]) => {
                const enrichedTabItems: RecordHeaderTabItem[] = tabItems.map(tabItem => ({
                    ...tabItem,
                    extras: {
                        ...tabItem.extras,
                        queryParams: {
                            ...tabItem.extras?.queryParams,
                            overview: overview?.overview
                        }
                    }
                }));
                return enrichedTabItems;
            }),
            distinctUntilChanged(isEqual)
        );
    }

    private initIndex(): void {
        this.index$ = combineLatest([this.overview$, this.recordId$]).pipe(
            switchMap(([overview, recordId]) => this.sandbox.getRecordIndex(overview?.overview!, recordId)),
            isNotUndefined(),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );
    }

    private initQueryContextElements(): void {
        this.primaryNav$ = this.overview$.pipe(
            map(overviewOptions => this.sandbox.getPrimaryNavItems(overviewOptions)),
            takeUntil(this.destroy$)
        );

        this.secondaryNav$ = combineLatest([this.overview$, this.index$]).pipe(
            takeUntil(this.destroy$),
            switchMap(([viewOptions, index]) => {
                if (!viewOptions) {
                    return of([]);
                }

                return this.sandbox.getSecondaryNavItems(viewOptions, index);
            })
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.recordId$.complete();
        this.tabItems$.complete();
    }

    getRouterLink(route: string): string[] {
        let parentRoute: string = route;

        if (this.router.url.includes('edit')) {
            parentRoute = '../' + parentRoute;
        }

        return [parentRoute];
    }

    shouldEnableNavigationItem(route: string): boolean {
        if (!this.isEditing) {
            return true;
        }

        if (this.router.url.includes('edit')) {
            const currentRoute: string = this.router.createUrlTree([route]).toString();

            if (this.router.url.includes(currentRoute)) {
                return true;
            }
        }

        return false;
    }
}
